import getRequest from "util/APIHelperGet";
import { baseUrl } from "util/APIBaseUrl";

export type AnalyticsEventType = "PROFILE_VIEW" | "SHORTLIST_ADDED" | "SHORTLIST_REMOVED";

export interface AnalyticsEventInput {
  eventType: AnalyticsEventType;
  talentId: string;
  opportunityId?: string;
  /** Active time-on-profile in milliseconds (PROFILE_VIEW only). */
  activeDurationMs?: number;
  source?: string;
}

export interface ShortlistsBlock {
  total: number;
  currentlyShortlisted: number;
  byActorType?: Record<string, number>;
}

export interface ViewsBlock {
  total: number;
  uniqueViewers: number;
  byActorType?: Record<string, number>;
  avgActiveDurationMs: number;
}

export interface TalentAnalyticsResponse {
  talentId: string;
  window: string;
  views?: ViewsBlock;
  shortlists?: ShortlistsBlock;
  lastLoginAt?: string | null;
  lastProfileUpdateAt?: string | null;
}

interface AnalyticsQueryOptions {
  window?: "7d" | "30d" | "90d" | "all";
  metrics?: string;
}

/**
 * Fire-and-forget ingest of a portal analytics event. Routes through the portal
 * backend (which forwards to the marketplace with the API key). Analytics must
 * never disrupt the UX, so failures are swallowed.
 *
 * `keepalive` lets the request survive page unload — used when a PROFILE_VIEW is
 * finalized as the customer navigates away.
 */
export async function ingestAnalyticsEvent(
  event: AnalyticsEventInput,
  { keepalive = false }: { keepalive?: boolean } = {}
): Promise<void> {
  try {
    const accessToken = localStorage.getItem("accessToken");
    await fetch(`${baseUrl}/talent-analytics/events`, {
      method: "POST",
      keepalive,
      headers: {
        "Content-Type": "application/json",
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(event),
    });
  } catch {
    // Swallow — analytics failures must not affect the customer experience.
  }
}

/**
 * Fetch a talent's analytics scoped to the authenticated customer's own activity.
 * Used to derive shortlist state for the heart toggle. Returns null on failure.
 */
export async function fetchTalentAnalytics(
  talentId: string,
  { window, metrics }: AnalyticsQueryOptions = {}
): Promise<TalentAnalyticsResponse | null> {
  const queryParams = new URLSearchParams();
  if (window) queryParams.append("window", window);
  if (metrics) queryParams.append("metrics", metrics);
  const qs = queryParams.toString();
  const url = `${baseUrl}/talent-analytics/profiles/${talentId}${qs ? `?${qs}` : ""}`;

  try {
    const { ok, payload } = await getRequest(url);
    if (!ok) return null;
    return payload as TalentAnalyticsResponse;
  } catch {
    return null;
  }
}
